import { Upload } from "lucide-react";
import { Alert, Box, Typography } from "@mui/material";
import { useUploader } from "components/creation/FileUploader";
import { ReactNode, useState } from "react";
import { useCreateCalendarIntegration } from "services/calendar";
import { UploadAttachment } from "protogen/common_pb";
import { PlainMessage } from "@bufbuild/protobuf";

export default ({
  onSuccess,
  onFailure,
  instructions,
  familyRef,
}: {
  onSuccess: (url: string) => void;
  onFailure: () => void;
  instructions?: ReactNode;
  familyRef?: string;
}) => {
  const { onUpload, uploadsInProgress } = useUploader({
    maxUploadBytes: 49000000,
  });
  const [uploadError, setUploadError] = useState<string | null>(null);
  const {
    request: createRequest,
    error: createError,
    loading: createLoading,
  } = useCreateCalendarIntegration();

  const handleFileUpload = (files: FileList) => {
    setUploadError(null);
    if (!files || files.length === 0) {
      setUploadError("No file selected");
      return;
    }

    const file = files[0];

    // Validate file type
    if (!file.name.toLowerCase().endsWith(".ics")) {
      setUploadError("Invalid file type");
      return;
    }

    onUpload([file], undefined, (uploads) => {
      if (uploads && uploads.length === 1) {
        // Pass the S3 path/URL to onSuccess
        // onSuccess(uploads[0] || "");
        addCalendar(uploads[0]);
      } else {
        setUploadError("Error uploading file");
      }
    });
  };
  const addCalendar = async (
    uploadAttachment: PlainMessage<UploadAttachment>,
  ) => {
    const resp = await createRequest({
      calendarType: "file",
      accessCode: "",
      accessScope: "",
      accessRedirectUri: "",
      calendarUrl: "",
      familyRef: familyRef || "",
      uploadedFile: uploadAttachment,
    });
    if (resp) {
      onSuccess("");
    } else {
      onFailure();
    }
  };
  const loading = uploadsInProgress || createLoading;
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {instructions}
      <Box
        sx={{
          border: "2px dashed #ccc",
          borderRadius: 2,
          p: 3,
          textAlign: "center",
          cursor: "pointer",
          "&:hover": {
            backgroundColor: "#f5f5f5",
          },
        }}
      >
        <input
          type="file"
          accept=".ics"
          onChange={(e) => handleFileUpload(e.target.files!)}
          style={{ display: "none" }}
          id="calendar-file-upload"
          disabled={loading}
        />
        <label htmlFor="calendar-file-upload" style={{ cursor: "pointer" }}>
          <Upload size={48} color="#666" />
          <Typography variant="h6" sx={{ mt: 1 }}>
            Upload ICS Calendar File
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Click to select an .ics file from your computer
          </Typography>
        </label>
      </Box>
      {uploadError ||
        (createError && <Alert>{uploadError || createError}</Alert>)}
    </Box>
  );
};
